import { useEffect, useMemo } from "react";

export const SITE_URL = "https://yono-habitat.com";
export const DEFAULT_OG =
  "/images/9y2itsq4-image-1.webp";

const DEFAULT_TITLE = "YONO Habitat | Habitat bois modulaire B2B";
const JSONLD_ID = "page-jsonld";

/**
 * Ensure a URL used in og/twitter/JSON-LD is absolute.
 * Open Graph and most crawlers (Facebook, LinkedIn, WhatsApp, X) require
 * fully-qualified https URLs; relative paths silently break link previews.
 */
function toAbsoluteUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`;
}


function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const match = selector.match(/\[(.+?)="(.+?)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

function applyMeta({ fullTitle, description, canonical, image, keywords }) {
  document.title = fullTitle;
  setMeta('meta[name="description"]', "content", description);
  if (keywords) setMeta('meta[name="keywords"]', "content", keywords);
  setLink("canonical", canonical);
  setMeta('meta[property="og:title"]', "content", fullTitle);
  setMeta('meta[property="og:description"]', "content", description);
  setMeta('meta[property="og:url"]', "content", canonical);
  setMeta('meta[property="og:image"]', "content", image);
  setMeta('meta[name="twitter:title"]', "content", fullTitle);
  setMeta('meta[name="twitter:description"]', "content", description);
  setMeta('meta[name="twitter:image"]', "content", image);
}

/**
 * useSEO — keep meta tags, canonical, og, JSON-LD in sync per page.
 */
export default function useSEO({
  title,
  description,
  path = "/",
  ogImage,
  jsonLd,
  keywords,
} = {}) {
  const jsonLdSerialized = useMemo(
    () => (jsonLd ? JSON.stringify(jsonLd) : null),
    [jsonLd]
  );

  useEffect(() => {
    const fullTitle = title ? `${title} | YONO Habitat` : DEFAULT_TITLE;
    const canonical = `${SITE_URL}${path}`;
    const image = toAbsoluteUrl(ogImage || DEFAULT_OG);

    applyMeta({ fullTitle, description, canonical, image, keywords });

    if (jsonLdSerialized) {
      setJsonLd(JSONLD_ID, JSON.parse(jsonLdSerialized));
    } else {
      removeJsonLd(JSONLD_ID);
    }
  }, [title, description, path, ogImage, jsonLdSerialized, keywords]);
}
